import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

function InventoryUpload() {
  const [vendors, setVendors] = useState([]); // Assume vendors are fetched or predefined
  const [createdPlans, setCreatedPlans] = useState([]); // Plans state for storing the plans from localStorage
  const [viewMode, setViewMode] = useState('upload');
  const [fileData, setFileData] = useState([]); // Hold the parsed file data
  const [selectedVendor, setSelectedVendor] = useState('');
  const [inventoryType, setInventoryType] = useState('single');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [iccid, setIccid] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const [showDetails, setShowDetails] = useState(null);

  // Load data from localStorage on component mount
  useEffect(() => {
    const storedVendors = JSON.parse(localStorage.getItem("vendors"));
    const storedPlans = JSON.parse(localStorage.getItem("createdPlans"));
    if (storedVendors) setVendors(storedVendors);
    if (storedPlans) setCreatedPlans(storedPlans);

    // Check for previously uploaded inventory data in localStorage
    const uploadedInventoryData = JSON.parse(localStorage.getItem('uploadedInventoryData'));
    if (uploadedInventoryData) {
      setFileData(uploadedInventoryData);
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsedData = XLSX.utils.sheet_to_json(sheet); // Load all rows
  
      // Add the selected information (vendor, plan, region, etc.)
      const updatedData = parsedData.map((item) => ({
        ...item,
        vendorName: selectedVendor,
        plan: selectedPlan,
        region: selectedRegion,
        MSSIDN: activationCode,
        iccid: iccid,
      }));
  
      // Get previously uploaded data from localStorage
      const previousData = JSON.parse(localStorage.getItem('uploadedInventoryData')) || [];
  
      // Prevent duplicates by checking if the current row exists in the previous data
      const mergedData = [
        ...previousData,
        ...updatedData.filter((newItem) => {
          return !previousData.some(
            (existingItem) => existingItem.iccid === newItem.iccid && existingItem.activationCode === newItem.activationCode
          );
        })
      ];
  
      // Update localStorage with the merged data
      localStorage.setItem('uploadedInventoryData', JSON.stringify(mergedData));
      setFileData(mergedData); // Update state
    };
    reader.readAsArrayBuffer(file);
  };
  
  const handleSaveData = () => {
    let updatedFileData = [...fileData];
  
    if (inventoryType === "single") {
      const newEntry = {
        vendorName: selectedVendor,
        plan: selectedPlan,
        region: selectedRegion,
        activationCode: activationCode,
        iccid: iccid,
      };
  
      // Check if the single entry already exists in fileData before adding it
      if (!updatedFileData.some((item) => item.iccid === newEntry.iccid && item.activationCode === newEntry.activationCode)) {
        updatedFileData.push(newEntry); // Add new single inventory entry only if not a duplicate
      }
    }
  
    localStorage.setItem('uploadedInventoryData', JSON.stringify(updatedFileData));
    setFileData(updatedFileData); // Update state
    setUploaded(true);
    setViewMode('upload');
    toast.error('Inventory Uploaded Successfully!');
  };
  

  const downloadSampleCSV = () => {
    const link = document.createElement('a');
    link.href = 'https://docs.google.com/spreadsheets/d/10NH04vV_3EqxUM9d7vSffhlhYz5NjvuR/edit?usp=sharing&ouid=109694045202780713005&rtpof=true&sd=true&export=download';
    link.download = 'sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDetails = (index) => {
    setShowDetails(showDetails === index ? null : index);
  };

  return (
    <div className="bg-white p-4">
      <h2 className="text-5xl font-bold text-gray-700 m -2">Upload Inventory</h2>

      {/* Upload Section */}
      {viewMode === 'upload' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <button
              className={`px-4 py-2 rounded-full font-semibold transition-colors duration-300 ${inventoryType === 'single' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setInventoryType('single')}
            >
              Single Inventory
            </button>
            <button
              className={`px-4 py-2 rounded-full font-semibold transition-colors duration-300 ${inventoryType === 'bulk' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setInventoryType('bulk')}
            >
              Bulk Inventory
            </button>
          </div>

          {/* Vendor Selection */}
          <div>
            <label htmlFor="vendorSelect" className="block text-sm font-medium text-gray-700">
              Select Vendor
            </label>
            <select
              id="vendor"
              onChange={(e) => setSelectedVendor(e.target.value)}
              value={selectedVendor}
              className="w-full p-2 border rounded-md"
            >
              <option value="">Select Vendor</option>
              {vendors.map((vendor, index) => (
                <option key={index} value={vendor.name}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>

          {/* Inventory Type Specific Fields */}
          {inventoryType === 'single' && (
            <>
              {/* Single Inventory Fields */}
              <div className="flex gap-2">
                <div className="flex flex-col w-full">
                  <label htmlFor="typeSelect" className="block text-sm font-medium text-gray-700">
                    Select Type (Local/Regional)
                  </label>
                  <select
                    id="typeSelect"
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    value={selectedRegion}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select Type</option>
                    <option value="local">Local</option>
                    <option value="regional">Regional</option>
                  </select>
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="planSelect" className="block text-sm font-medium text-gray-700">
                    Select Plan
                  </label>
                  <select
                    id="plan"
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    value={selectedPlan}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select Plan</option>
                    {createdPlans.map((plan, index) => (
                      <option key={index} value={plan.name}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Activation Code and iccid Fields */}
              <div className="flex gap-2">
                <div className="flex flex-col w-full">
                  <label htmlFor="activationCode" className="block text-sm font-medium text-gray-700">
                    Activation Code
                  </label>
                  <input
                    id="activationCode"
                    type="text"
                    value={activationCode}
                    onChange={(e) => setActivationCode(e.target.value)}
                    placeholder="Activation Code"
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="iccid" className="block text-sm font-medium text-gray-700">
                    ICCID
                  </label>
                  <input
                    id="iccid"
                    type="text"
                    value={iccid}
                    onChange={(e) => setIccid(e.target.value)}
                    placeholder="iccid"
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex flex-col w-full">
                  <label htmlFor="MSSIDN" className="block text-sm font-medium text-gray-700">
                    MSSIDN
                  </label>
                  <input
                    id="MSSIDN"
                    type="text"
                    placeholder="MSSIDN"
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="PIN" className="block text-sm font-medium text-gray-700">
                    PIN
                  </label>
                  <input
                    id="PIN"
                    type="text"
                    placeholder="PIN"
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div className="flex flex-col w-full">
                  <label htmlFor="PUK" className="block text-sm font-medium text-gray-700">
                    PUK
                  </label>
                  <input
                    id="PUK"
                    type="text"
                    placeholder="PUK"
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>
            </>
          )}

          {inventoryType === 'bulk' && (
            <>
              {/* Bulk Inventory Fields */}
              <div className="flex gap-2">
                <div className="flex flex-col w-full">
                  <label htmlFor="typeSelect" className="block text-sm font-medium text-gray-700">
                    Select Type (Local/Regional)
                  </label>
                  <select
                    id="typeSelect"
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    value={selectedRegion}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select Type</option>
                    <option value="local">Local</option>
                    <option value="regional">Regional</option>
                  </select>
                </div>

                <div className="flex flex-col w-full">
                  <label htmlFor="planSelect" className="block text-sm font-medium text-gray-700">
                    Select Plan
                  </label>
                  <select
                    id="planSelect"
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    value={selectedPlan}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Select Plan</option>
                    {createdPlans.map((plan, index) => (
                      <option key={index} value={plan.planName}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* File Upload Button */}
              <div>
                <label htmlFor="fileUpload" className="block text-sm font-medium text-gray-700">
                  Upload Inventory File
                </label>
                <input
                  id="fileUpload"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={handleSaveData}
              className="bg-[#117575] rounded-full p-4 text-white shadow-md hover:bg-[#1cbebe] transition duration-200 "
            >
              Upload Inventory
            </button>

            <button
              onClick={downloadSampleCSV}
              className="bg-yellow-500 text-white rounded-full  px-4 py-2  hover:opacity-80"
            >
              Sample CSV
            </button>

            <button
              onClick={() => setViewMode('uploaded')}
              className="bg-teal-500 rounded-full p-4 text-white shadow-md hover:bg-[#1cbebe] transition duration-200"
            >
              View details
            </button>
          </div>
        </div>
      )}

      {/* Uploaded Inventory Details */}
      {viewMode === 'uploaded' && (
        <div className="mt-6">
        <div className="flex justify-between items-center w-full">
        <h3 className="text-2xl font-bold">Uploaded Inventory Details</h3>
          
          <button
              onClick={() => setViewMode('upload')}
              className="bg-teal-500 rounded-full p-2 text-white shadow-md hover:bg-[#1cbebe] transition duration-200"
            >
              Upload Inventory
            </button>
            </div>
          {fileData.length > 0 ? (
  <table className="w-full table-auto mt-4 border-collapse border border-gray-300">
    <thead>
      <tr className="bg-gray-100">
        <th className="border border-gray-300 p-2">Vendor Name</th>
        <th className="border border-gray-300 p-2">Plan</th>
        <th className="border border-gray-300 p-2">Region</th>
        <th className="border border-gray-300 p-2">ICCID</th>
        <th className="border border-gray-300 p-2">Activation Code</th>
      </tr>
    </thead>
    <tbody>
      {fileData.map((item, index) => (
        <tr key={index}>
          <td className="border border-gray-300 p-2">{item.vendorName}</td>
          <td className="border border-gray-300 p-2">{item.plan}</td>
          <td className="border border-gray-300 p-2">{item.region}</td>
          <td className="border border-gray-300 p-2">{item.iccidcountry ||item.iccid}</td>
          <td className="border border-gray-300 p-2">{item.activationCode}</td>
        </tr>
      ))}
    </tbody>
  </table>
) : (
  <p className="mt-4 text-red-500">Sorry, no entry is there. Please upload.</p>
)}

        </div>
      )}
    </div>
  );
}

export default InventoryUpload;
